import { parseCalendarDate } from "@/utils/date";
import { buildCapacityEventsFromTimelineEvents } from "@/utils/chart/customer-projection.utils";
import type { FinancialEvent } from "@/types/startup.types";

export function mapInvestorPayloadToChartInput(payload: Record<string, unknown>) {
  const expenses = (payload.expenses as Array<Record<string, unknown>>) ?? [];
  const revenues = (payload.revenues as Array<Record<string, unknown>>) ?? [];
  const events = (payload.events as Array<Record<string, unknown>>) ?? [];
  const customerProjections =
    (payload.customerProjections as Array<Record<string, unknown>>) ?? [];
  const subscriptionTiers =
    (payload.subscriptionTiers as Array<Record<string, unknown>>) ?? [];
  const customerGrowthScenarios =
    (payload.customerGrowthScenarios as Array<Record<string, unknown>>) ?? [];
  const acquisitionCostModels =
    (payload.acquisitionCostModels as Array<Record<string, unknown>>) ?? [];
  const capacityGroups = (payload.capacityGroups as Array<Record<string, unknown>>) ?? [];
  const teamMembers = (payload.teamMembers as Array<Record<string, unknown>>) ?? [];

  const startDate =
    parseCalendarDate(payload.startDate as string) ??
    parseCalendarDate(payload.foundedDate as string) ??
    new Date("2024-01-01");
  const endDate =
    parseCalendarDate(payload.endDate as string) ?? new Date("2028-12-31");

  const chartData = {
    startDate,
    endDate,
    projectEndDate: parseCalendarDate(payload.endDate as string) ?? undefined,
    initialBalance:
      Number(payload.initialFunding ?? payload.initialBalance ?? 0) || 0,
    expenses: expenses.map((expense) => ({
      ...expense,
      isOneTime: Boolean(expense.isOneTime),
      monthlyAmount: expense.isOneTime
        ? 0
        : Number(expense.monthlyAmount ?? expense.amount ?? 0),
      oneTimeAmount: expense.oneTimeAmount
        ? Number(expense.oneTimeAmount)
        : undefined,
      dayOfMonth: Number(expense.dayOfMonth ?? 1),
      startDate: expense.startDate
        ? parseCalendarDate(String(expense.startDate)) ?? undefined
        : undefined,
      endDate: expense.endDate
        ? parseCalendarDate(String(expense.endDate)) ?? undefined
        : undefined,
    })),
    revenues: revenues.map((revenue) => ({
      ...revenue,
      monthlyAmount: Number(revenue.monthlyAmount ?? revenue.amount ?? 0),
      dayOfMonth: Number(revenue.dayOfMonth ?? 1),
      startDate: revenue.startDate
        ? parseCalendarDate(String(revenue.startDate)) ?? undefined
        : revenue.date
          ? parseCalendarDate(String(revenue.date)) ?? undefined
          : undefined,
      endDate: revenue.endDate
        ? parseCalendarDate(String(revenue.endDate)) ?? undefined
        : undefined,
    })),
    events: events.map((event) => ({
      ...event,
      date: parseCalendarDate(String(event.date)) ?? new Date(String(event.date)),
    })),
  };

  const customerProjectionData =
    customerProjections.length > 0 || subscriptionTiers.length > 0
      ? {
          projections: customerProjections,
          subscriptionTiers,
          growthScenarios: customerGrowthScenarios,
          acquisitionCostModels,
          capacityGroups,
          capacityEvents: buildCapacityEventsFromTimelineEvents(
            events.map((event) => ({
              ...(event as unknown as FinancialEvent),
              date: parseCalendarDate(String(event.date)) ?? new Date(String(event.date)),
            })),
          ),
        }
      : undefined;

  return {
    chartData: {
      ...chartData,
      customerProjectionData,
    },
    teamMembers,
    businessPlan: payload.businessPlan ?? null,
    startupMeta: {
      name: String(payload.name ?? "Startup"),
      tagline: payload.tagline ? String(payload.tagline) : undefined,
      description: payload.description ? String(payload.description) : undefined,
      website: payload.website ? String(payload.website) : undefined,
      address: payload.address,
      phone: payload.phone ? String(payload.phone) : undefined,
    },
  };
}
